// Client-side image compression for the quick-list upload flow.
//
// iPhone HEIC photos are 4-10MB each — uploading them raw is the single
// biggest source of latency in the listing flow. We resize to max 1920px on
// the longest edge and re-encode as JPEG ~q82 in-browser, which brings them
// to ~300-500KB. The compressed file is what we POST to /api/quicklist/upload.
//
// Compression runs in parallel for multiple files (one Canvas decode per
// thread of cooperative concurrency — browsers will batch these anyway).
//
// Fallback: if the browser can't decode the input (e.g. HEIC on desktop
// Chrome with no HEIC support), we return the original file and let the
// backend deal with it — better than blocking the upload.

const MAX_DIM = 1920;
const JPEG_QUALITY = 0.82;

/**
 * Compress a single image file. Returns a new File (always JPEG) or the
 * original if anything fails.
 *
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function compressImage(file) {
  // Skip compression entirely for small files (<700KB) — the upload itself is
  // already fast and decoding/re-encoding wastes battery.
  if (file.size < 700 * 1024 && /jpe?g/i.test(file.type)) {
    return file;
  }

  let url;
  try {
    const img = new Image();
    url = URL.createObjectURL(file);
    img.src = url;
    await img.decode();

    const longest = Math.max(img.width, img.height);
    const scale = Math.min(MAX_DIM / longest, 1);
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, w, h);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
    );
    if (!blob) return file;

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'photo';
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
  } catch (e) {
    // Decode failure (HEIC on a browser without HEIC support, corrupt file).
    // Return original — backend has a fallback compressor too.
    return file;
  } finally {
    if (url) URL.revokeObjectURL(url);
  }
}

/**
 * Compress a list of files in parallel. Yields a new File for each entry,
 * preserving order.
 *
 * @param {File[]} files
 * @returns {Promise<File[]>}
 */
export function compressImages(files) {
  return Promise.all(Array.from(files).map(compressImage));
}
